import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '@/components/layout/Layout';
import { NewVideoLayout } from '@/components/video-editor/NewVideoLayout';
import { Header } from '@/components/layout/Header';
import { VideoSelectorButton } from '@/components/video-editor/VideoSelectorButton';
import { VideoPreviewSection } from '@/components/video-editor/VideoPreviewSection';
import { VideoLibraryModalWithFolders } from '@/components/video-editor/VideoLibraryModalWithFolders';
import { ModularVideoEditorContainer } from '@/components/video-editor/ModularVideoEditorContainer';
import { usePersonalization } from '@/hooks/use-personalization';
import { useParams, useNavigate } from 'react-router-dom';
import { useCustomAuth } from '@/contexts/CustomAuthContext';
import { getFeatureDefinition } from '@/components/feature-list';

type VideoStatus = 'empty' | 'uploading' | 'processing' | 'ready' | 'error';

const Personalization = () => {
  useLanguage();
  const { videoId } = useParams<{ videoId: string }>();
  const navigate = useNavigate();
  const loadedVideoIdRef = useRef<string | null>(null);
  const { user } = useCustomAuth();

  const {
    features,
    setFeature,
    currentFeature,
    setCurrentFeature,
    modalTitle,
    modalSubtitle,
    showUnsavedDialog,
    setShowUnsavedDialog,
    showEmbedModal,
    setShowEmbedModal,
    isSaved,
    handleFeatureClick,
    activeEditingPanel,
    handleCloseEditor,
    handleApplyChanges,
    confirmNavigation,
    isLoading,
    isInitializing,
    hasAutoLoaded,
    handleVideoSelect,
    processedVideos,
    showVideoLibraryModal,
    setShowVideoLibraryModal,
    handleSave,
    isSaving,
    hasUnsavedChanges,
  } = usePersonalization();

  // Estados de vídeo
  const [videoStatus, setVideoStatus] = useState<VideoStatus>('empty');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLoadingFromUrl, setIsLoadingFromUrl] = useState(false);
  const [lastEditedFeature, setLastEditedFeature] = useState<string | null>(null);

  // Verificar se vídeo foi carregado da URL (para evitar reload desnecessário)
  const hasVideoIdInUrl = Boolean(videoId);

  // Sincronizar videoStatus com features.videoUrl
  useEffect(() => {
    if (isLoading || isInitializing) {
      setVideoStatus('processing');
    } else if (features.videoUrl) {
      setVideoStatus('ready');
    } else {
      setVideoStatus('empty');
    }
  }, [features.videoUrl, isLoading, isInitializing]);
  
  // Carregar vídeo específico da URL (modo fixo)
  useEffect(() => {
    // Aguardar o carregamento inicial terminar antes de qualquer ação
    if (!videoId || isInitializing) {
      if (videoId) setIsLoadingFromUrl(true);
      return;
    }

    // Se já carregamos este vídeo, não recarregar
    if (loadedVideoIdRef.current === videoId) {
      setIsLoadingFromUrl(false);
      return;
    }

    // Tenta encontrar o vídeo na lista local para aproveitar metadados (título, thumbnail)
    // Se não encontrar, cria um objeto mínimo — loadVideo busca os dados completos via API
    const video = processedVideos.find(v => v.id === videoId)
      || { id: videoId, title: '', videoUrl: '', status: 'active' };

    // Mostrar loading enquanto carrega
    setIsLoadingFromUrl(true);

    // Carregar o vídeo e marcar como carregado
    loadedVideoIdRef.current = videoId;
    handleVideoSelect(video).finally(() => {
      setIsLoadingFromUrl(false);
    });
  }, [videoId, isInitializing]);
  
  // Reset ref quando sair da página ou mudar de vídeo
  useEffect(() => {
    // Se não houver videoId na URL (saiu da página), limpar ref
    if (!videoId) {
      loadedVideoIdRef.current = null;
    }
    
    // Cleanup: limpar ref quando componente é desmontado
    return () => {
      loadedVideoIdRef.current = null;
    };
  }, [videoId]);
  
  // Abrir modal IMEDIATAMENTE se não houver vídeo selecionado
  useEffect(() => {
    // Se há videoId na URL, não abre modal automaticamente pois vai carregar o vídeo
    if (hasVideoIdInUrl) return;

    // Abre modal imediatamente quando vídeos estão prontos e não há vídeo selecionado
    if (!isLoading && !isInitializing && !features.videoUrl && processedVideos.length > 0) {
      setShowVideoLibraryModal(true);
    }
  }, [isLoading, isInitializing, features.videoUrl, processedVideos.length, setShowVideoLibraryModal, hasVideoIdInUrl]);

  const hasVideo = videoStatus === 'ready' && Boolean(features.videoUrl);
  const hasVideoUrl = Boolean(features.videoUrl); // Para o sidebar, só precisa ter URL

  // Determinar se está em estado de loading
  const isCurrentlyLoading = isInitializing || isLoadingFromUrl || isLoading || (hasVideoIdInUrl && !features.videoUrl);

  // Buscar dados do vídeo atual - por videoId primeiro, depois por videoUrl
  // Fallback para dados do features quando o vídeo não está na lista local (ex: vídeo em pasta)
  const currentVideo = features.videoId
    ? (processedVideos.find(v => v.id === features.videoId) || {
        id: features.videoId,
        title: features.title || '',
        thumbnailUrl: features.videoFrame || features.pauseThumbnail?.imageUrl || features.initialThumbnail?.imageUrl || null,
      })
    : features.videoUrl
      ? processedVideos.find(v => v.videoUrl === features.videoUrl) || null
      : null;

  // Handler para trocar vídeo - abre modal de seleção
  const handleChangeVideo = () => {
    setShowVideoLibraryModal(true);
  };

  // Handler para trocar vídeo pelo ID (usado pelo VideoSelectorV2)
  const handleVideoSelectById = (newVideoId: string | null) => {
    if (!newVideoId) return;

    const video = processedVideos.find(v => v.id === newVideoId);
    if (video) {
      handleVideoSelectWithNavigation(video);
    }
  };

  // Handler para selecionar vídeo e atualizar URL
  const handleVideoSelectWithNavigation = async (video: any) => {
    // Fechar modal imediatamente
    setShowVideoLibraryModal(false);

    // Mostrar loading
    setIsLoadingFromUrl(true);

    // Atualizar ref para não recarregar via useEffect de URL
    loadedVideoIdRef.current = video.id;

    try {
      // Selecionar o vídeo
      await handleVideoSelect(video);

      // Atualizar URL para refletir o vídeo selecionado
      navigate(`/personalizations/${video.id}`, { replace: true });
    } finally {
      setIsLoadingFromUrl(false);
    }
  };

  const handleCopyEmbed = () => {
    setShowEmbedModal(true);
  };

  // Breadcrumb do header: "Personalização / <último recurso aberto>"
  const handleEditorFeatureClick = (featureId: string) => {
    setLastEditedFeature(featureId);
    handleFeatureClick(featureId);
  };

  const lastEditedFeatureLabel = lastEditedFeature
    ? getFeatureDefinition(lastEditedFeature)?.title || lastEditedFeature
    : translateInterfaceText("Visão geral");

  const videoTitle = features.title || currentVideo?.title || translateInterfaceText("Vídeo sem título");

  return (
    <Layout
      hasUnsavedChanges={hasUnsavedChanges}
      onSave={handleSave}
      isSaving={isSaving}
      isSaved={isSaved}
      videoId={features.videoId}
      videoName={features.title || currentVideo?.title}
      userId={user?.id}
      haveHeadline={features.headline?.enabled}
      isHTMLSelected={features.headline?.htmlMode?.enabled}
      headlineHtmlCode={features.headline?.htmlMode?.code}
      headlineShowThroughout={features.headline?.timing?.showThroughout}
      headlineInitialDelay={features.headline?.timing?.start}
      headlineEndDelay={features.headline?.timing?.end}
    >
      <ModularVideoEditorContainer
        features={features}
        setFeature={setFeature}
        currentFeature={currentFeature}
        setCurrentFeature={setCurrentFeature}
        modalTitle={modalTitle}
        modalSubtitle={modalSubtitle}
        showUnsavedDialog={showUnsavedDialog}
        setShowUnsavedDialog={setShowUnsavedDialog}
        confirmNavigation={confirmNavigation}
        showEmbedModal={showEmbedModal}
        setShowEmbedModal={setShowEmbedModal}
        isSaved={isSaved}
        activeEditingPanel={activeEditingPanel}
        onCloseEditor={handleCloseEditor}
        onApplyChanges={handleApplyChanges}
      >
        <NewVideoLayout
          features={features}
          onFeatureClick={handleEditorFeatureClick}
          showResourcesSidebar={hasVideoUrl && !isCurrentlyLoading}
          activeEditingPanel={activeEditingPanel}
          currentVideo={currentVideo as any}
          onChangeVideo={handleChangeVideo}
        >
          {/* Header no topo do card (o stage abaixo tem scroll próprio) - oculto durante loading */}
          {!isCurrentlyLoading && (
            <Header
              personalizationMode={hasVideo}
              personalizationBreadcrumbLabel={lastEditedFeatureLabel}
              showEmbedButton={hasVideo}
              onEmbedClick={handleCopyEmbed}
              hasUnsavedChanges={hasUnsavedChanges}
              onSave={handleSave}
              isSaving={isSaving}
              isSaved={isSaved}
              showLogo={!hasVideo}
              videoId={features.videoId}
              videoName={features.title || currentVideo?.title}
              userId={user?.id}
              haveHeadline={features.headline?.enabled}
              isHTMLSelected={features.headline?.htmlMode?.enabled}
              headlineHtmlCode={features.headline?.htmlMode?.code}
              headlineShowThroughout={features.headline?.timing?.showThroughout}
              headlineInitialDelay={features.headline?.timing?.start}
              headlineEndDelay={features.headline?.timing?.end}
            />
          )}

          {/* Stage: canvas com scroll próprio (barra oculta); o card de preview
              fica alinhado ao topo e rola se for mais alto que a área.
              max-lg:pt-16 compensa o MobileHeader fixo (64px) do Layout, que não
              aplica pt-[64px] nas rotas de personalização. */}
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain bg-[var(--editor-canvas)] max-lg:pt-16 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {isCurrentlyLoading ? (
              /* Loading initial video */
              <div className="flex flex-col items-center justify-center min-h-full gap-4">
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
                </div>
                <div className="text-center">
                  <p className="text-[16px] font-medium text-foreground">{translateInterfaceText("Carregando vídeo...")}</p>
                  <p className="text-[14px] text-muted-foreground mt-1">{translateInterfaceText("Aguarde um momento")}</p>
                </div>
              </div>
            ) : !hasVideo ? (
              /* No video selected: Show empty state with call to action */
              <div className="flex flex-col items-center justify-center min-h-full gap-6 p-8">
                <div className="w-20 h-20 rounded-full bg-[#EEF4FF] flex items-center justify-center">
                  <svg className="w-10 h-10 text-[#3B82F6]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="text-center max-w-md">
                  <h2 className="text-[24px] font-semibold text-[#111827] mb-2">
                    {translateInterfaceText("Nenhum vídeo selecionado")}</h2>
                  <p className="text-[16px] text-[#6B7280] mb-6">
                    {translateInterfaceText("Selecione um vídeo da sua biblioteca para começar a personalizar e configurar seus recursos.")}</p>
                  <button
                    onClick={() => setShowVideoLibraryModal(true)}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-[10px] font-medium transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    {translateInterfaceText("Selecionar Vídeo")}</button>
                </div>
              </div>
            ) : (
              /* Video selected: Show stage with preview */
              <VideoPreviewSection
                features={features}
                setFeature={setFeature}
                videoStatus={videoStatus}
                setVideoStatus={setVideoStatus}
                uploadProgress={uploadProgress}
                setUploadProgress={setUploadProgress}
                onReplaceVideo={handleChangeVideo}
                onCopyEmbed={handleCopyEmbed}
                title={videoTitle}
              />
            )}
          </div>
        </NewVideoLayout>
      </ModularVideoEditorContainer>

      {/* Video Library Modal */}
      <VideoLibraryModalWithFolders
        open={showVideoLibraryModal}
        onOpenChange={setShowVideoLibraryModal}
        onSelect={handleVideoSelectWithNavigation}
        title={translateInterfaceText("Selecionar Vídeo para Personalizar")}
      />
    </Layout>
  );
};

export default Personalization;
